import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { GridRenderEditCellParams } from '@mui/x-data-grid';

export type AutocompleteOption = {
  value: string;
  label: string;
};

export interface AutocompleteEditCellProps extends GridRenderEditCellParams {
  options: AutocompleteOption[];
  placeholder?: string;
}

export function getOptionLabelFromValue(
  options: AutocompleteOption[],
  value: string | null | undefined
): string {
  if (value == null || value === '') return '';
  const opt = options.find(o => o.value === value);
  return opt ? opt.label : value;
}

export const AutocompleteEditCell: React.FC<AutocompleteEditCellProps> = params => {
  const { id, value, field, api, options, placeholder } = params;
  const raw = (value as string) ?? '';
  const option = options.find(o => o.value === raw);
  const autocompleteValue: AutocompleteOption | string = option ?? (raw || '');

  const [inputValue, setInputValue] = React.useState(
    () => getOptionLabelFromValue(options, raw) || raw || ''
  );

  const commitValue = React.useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        api.setEditCellValue({ id, field, value: '' });
        return;
      }
      const match = options.find(
        o => o.label.toLowerCase() === trimmed.toLowerCase()
      );
      const next = match ? match.value : trimmed;
      api.setEditCellValue({ id, field, value: next });
    },
    [api, field, id, options]
  );

  return (
    <Autocomplete
      freeSolo
      size="small"
      sx={{ minWidth: 200, mx: 0.5 }}
      options={options}
      value={autocompleteValue === '' ? null : autocompleteValue}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={opt => (typeof opt === 'string' ? opt : opt.label)}
      onChange={(_, newValue) => {
        const next =
          newValue == null
            ? ''
            : typeof newValue === 'string'
              ? newValue
              : (newValue as AutocompleteOption).value;
        api.setEditCellValue({ id, field, value: next });
        setInputValue(next ? getOptionLabelFromValue(options, next) : '');
      }}
      onBlur={() => {
        if (inputValue.trim()) commitValue(inputValue);
      }}
      onKeyDown={ev => {
        if (ev.key === 'Enter' && inputValue.trim()) {
          commitValue(inputValue);
        }
      }}
      renderInput={inputProps => (
        <TextField
          {...inputProps}
          variant="standard"
          placeholder={placeholder || ''}
        />
      )}
    />
  );
};

